import { useCallback, useEffect, useState } from 'react';

import { usePrevious } from '@/hooks/usePrevious';
import { useAppSelector } from '@/redux/hooks';
import {
    getShippingEstimateAttempted,
    getShippingEstimateIsTooExpensive,
} from '@/redux/modules/shipping/shipping.selectors';
import { ModalPageManager } from '@/types/modal/ModalPageManager.types';
import { useAlwaysShowExpensiveLiveShippingQuotesExperiment } from '@/utils/amplitude/experiments/hooks/useAlwaysShowExpensiveLiveShippingQuotes.experiment';

export enum CustomShippingRateModalPages {
    CalculateQuoteForm = 'calculateQuoteForm',
    TooExpensiveForm = 'tooExpensiveForm',
    TooExpensiveRequestSuccess = 'tooExpensiveSuccess',
}

export function useCustomShippingRateModalPages(
    itemId: number,
    isOpen: boolean
): ModalPageManager<CustomShippingRateModalPages> {
    const isInAlwaysShowExpensiveQuoteExperiment = useAlwaysShowExpensiveLiveShippingQuotesExperiment();

    const wasOpen = usePrevious(isOpen);
    const triedToCalculateEstimate = useAppSelector((state) => getShippingEstimateAttempted(state, itemId));
    const isTooExpensive = useAppSelector((state) => getShippingEstimateIsTooExpensive(state, itemId));
    const [currentPage, setCurrentPage] = useState(
        triedToCalculateEstimate && isTooExpensive && !isInAlwaysShowExpensiveQuoteExperiment
            ? CustomShippingRateModalPages.TooExpensiveForm
            : CustomShippingRateModalPages.CalculateQuoteForm
    );

    useEffect(() => {
        if (isOpen && !wasOpen) {
            setCurrentPage(
                triedToCalculateEstimate && isTooExpensive && !isInAlwaysShowExpensiveQuoteExperiment
                    ? CustomShippingRateModalPages.TooExpensiveForm
                    : CustomShippingRateModalPages.CalculateQuoteForm
            );
        }
    }, [isInAlwaysShowExpensiveQuoteExperiment, isOpen, isTooExpensive, triedToCalculateEstimate, wasOpen]);

    useEffect(() => {
        if (
            isOpen &&
            wasOpen &&
            isTooExpensive &&
            currentPage === CustomShippingRateModalPages.CalculateQuoteForm &&
            !isInAlwaysShowExpensiveQuoteExperiment
        ) {
            setCurrentPage(CustomShippingRateModalPages.TooExpensiveForm);
        }
    }, [currentPage, isInAlwaysShowExpensiveQuoteExperiment, isOpen, isTooExpensive, wasOpen]);

    const goToNextPage = useCallback(() => {
        setCurrentPage(CustomShippingRateModalPages.TooExpensiveRequestSuccess);
    }, []);

    return {
        currentPage,
        goToNextPage,
        setCurrentPage,
    };
}
